import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { PhysicianSupervisee } from '@uplift/api';
import { DateFormats } from '@uplift/constants';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useSupervisionMeetingQuery, useUserQuery } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';

import { Loader } from '../../../Loader';
import { Modal, ModalContent, ModalTitle } from '../../../Modal';


const Section: React.FC<{ label: string }> = ({ label, children }) => (
  <Box sx={{ py: 2 }}>
    <Typography variant="overline" color="secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
      {label}
    </Typography>
    {children}
  </Box>
);


export type SupervisionMeetingDetailModalProps = {
  open: boolean;
  onClose: () => void;
  meetingId: string | null;
  superviseeMatch: PhysicianSupervisee;
};


export const SupervisionMeetingDetailModal: React.VFC<SupervisionMeetingDetailModalProps> = ({ open, onClose, meetingId, superviseeMatch }) => {
  const l10n = useL10n();
  const currentUser = useCurrentuser();
  const timezone = useTimezone();
  const { data: meeting, isLoading } = useSupervisionMeetingQuery(currentUser.id, superviseeMatch.id, meetingId ?? '');
  const { data: supervisor } = useUserQuery(meeting?.collaborativePhysicianId);

  const prescriberLabel = superviseeMatch.prescriberType ? l10n.resources.provider.prescriberTypes[superviseeMatch.prescriberType] : '';
  const superviseeName = `${superviseeMatch.user.firstName} ${superviseeMatch.user.lastName}`;
  const superviseeDisplay = `${superviseeName}${prescriberLabel ? `, ${prescriberLabel}` : ''}`;
  const supervisorName = supervisor ? `${supervisor.firstName} ${supervisor.lastName}` : '';

  const submittedAt = meeting?.submittedAt ? CalendarDateService.date(meeting.submittedAt).tz(timezone) : null;

  return (
    <Modal open={open} maxWidth="md" fullWidth onClose={onClose}>
      <ModalTitle onClose={onClose} />
      <ModalContent>
        {isLoading || !meeting ? (
          <Loader />
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="h6">Supervision Meeting</Typography>
              <Typography sx={{ fontWeight: 600 }}>with {superviseeName}</Typography>
            </Box>
            <Divider />

            <Section label="Date of Meeting">
              <Typography>{CalendarDateService.date(meeting.meetingDate).tz(timezone).format(DateFormats.DATETIME)}</Typography>
            </Section>
            <Divider />

            <Section label="Participants">
              <Typography sx={{ fontWeight: 500 }}>Supervising physician: {supervisorName}</Typography>
              <Typography sx={{ fontWeight: 500 }}>Supervisee: {superviseeDisplay}</Typography>
              <Typography sx={{ fontWeight: 500 }}>Method of communication: {l10n.supervisees.meetingMethods[meeting.method]}</Typography>
            </Section>
            <Divider />

            <Section label="Duration">
              <Typography>{meeting.durationMinutes} minutes</Typography>
            </Section>
            <Divider />

            <Section label="Meeting Summary">
              <Typography>{meeting.summary}</Typography>
            </Section>

            {submittedAt?.isValid() && (
              <Typography sx={{ mt: 3 }} color="text.secondary">
                Signed and submitted by {supervisorName} on {CalendarDateService.format(submittedAt, DateFormats.DATETIME)}
              </Typography>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
